/**
 * Block Page
 * 
 * Displays a table of blocked users.
 */

import * as React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { AddBlockDialog } from "./AddBlockDialog";
import { BlockTable } from "./components/BlockTable";
import { UnblockDialog } from "./components/UnblockDialog";
import { useBlocks } from "./hooks/useBlocks";
import { useUnblock } from "./hooks/useUnblock";
import type { Block } from "@/services/blockService";

export function BlockPage() {
  const { blocks, loading, error, fetchBlocks, nextCursor, previousCursor, hasNext, hasPrevious } = useBlocks();

  // Pagination and search state
  const [search, setSearch] = React.useState("");
  const [cursorStack, setCursorStack] = React.useState<string[]>([]);

  // Unblock dialog state
  const [unblockDialogOpen, setUnblockDialogOpen] = React.useState(false);
  const [selectedBlock, setSelectedBlock] = React.useState<Block | null>(null);

  // Add block dialog state
  const [addBlockDialogOpen, setAddBlockDialogOpen] = React.useState(false);

  // Unblock logic
  const { unblocking, handleUnblock } = useUnblock({
    onSuccess: () => {
      setUnblockDialogOpen(false);
      setSelectedBlock(null);
      fetchBlocks({ search });
    },
  });

  const handleUnblockClick = React.useCallback((block: Block) => {
    setSelectedBlock(block);
    setUnblockDialogOpen(true);
  }, []);

  const handleUnblockConfirm = React.useCallback(async () => {
    if (selectedBlock) {
      await handleUnblock(selectedBlock);
    }
  }, [selectedBlock, handleUnblock]);

  // Handle search
  const handleSearch = React.useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setCursorStack([]); // Reset pagination on new search
    fetchBlocks({ search: searchTerm });
  }, [fetchBlocks]);

  // Handle next page
  const handleNextPage = React.useCallback(() => {
    if (nextCursor) {
      if (cursorStack.length === 0) {
        setCursorStack([""]);
      }
      setCursorStack((prev) => [...prev, nextCursor]);
      fetchBlocks({
        cursor: nextCursor,
        direction: "next",
        search,
      });
    }
  }, [nextCursor, cursorStack, search, fetchBlocks]);

  // Handle previous page
  const handlePreviousPage = React.useCallback(() => {
    if (previousCursor && cursorStack.length > 0) {
      const newStack = [...cursorStack];
      newStack.pop();
      setCursorStack(newStack);

      const cursor = newStack.length > 0 ? newStack[newStack.length - 1] : undefined;
      if (cursor) {
        fetchBlocks({
          cursor,
          direction: "next",
          search,
        });
      } else {
        fetchBlocks({ search });
      }
    }
  }, [previousCursor, cursorStack, search, fetchBlocks]);

  return (
    <div className="flex-1 space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Daftar Block</h1>
          <p className="text-muted-foreground">
            Daftar pengguna yang di-block
          </p>
        </div>
        <Button onClick={() => setAddBlockDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Block User
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <Card className="border-red-500">
          <CardContent className="pt-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Blocks Table */}
      <BlockTable
        blocks={blocks}
        loading={loading}
        onUnblock={handleUnblockClick}
        onSearch={handleSearch}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        search={search}
      />

      {/* Unblock Confirmation Dialog */}
      <UnblockDialog
        open={unblockDialogOpen}
        onOpenChange={setUnblockDialogOpen}
        block={selectedBlock}
        onConfirm={handleUnblockConfirm}
        loading={unblocking}
      />

      {/* Add Block Dialog */}
      <AddBlockDialog
        open={addBlockDialogOpen}
        onOpenChange={setAddBlockDialogOpen}
        onSuccess={() => {
          // Refresh blocks list after successful block
          fetchBlocks();
        }}
      />
    </div>
  );
}

